// Mesh — every peer of one room, connected and (optionally) authenticated.
//
// Owns the RoomClient and one PeerSession per remote peer, and applies the reconnection rules
// learned in the lab: a peer whose signaling socket drops is kept LEAVE_GRACE_MS (it may still
// be connected peer-to-peer, or be switching networks); a reconnect on either side triggers a
// resync + ICE restart; the browser's 'online' event too.
//
// With an auth key (share links), a peer is only "authed" once its DTLS-bound proof verifies,
// and nothing but 'auth' messages is accepted from it before that. Every new connection
// generation has new DTLS certificates, so authentication runs again on each 'ready'.
//
// Events (detail always has `peer`): 'peer' (known), 'authed', 'down', 'gone', 'auth-failed',
// 'ctl' {msg}, 'channel' {channel}, 'state' {state}.

import { RoomClient } from './signal';
import { PeerSession, type Signal } from './peer';
import { checkProof, makeProof, sdpFingerprint } from './auth';
import { randomHex } from './ids';

const LEAVE_GRACE_MS = 20_000;
// Messages and channels that arrive while the peer's proof is still being verified are held,
// then delivered in order once it verifies (dropped if it fails). Without this, a fast sender
// that already verified us sends its manifest before our own verification of it completes,
// and the manifest was lost (observed iPhone ↔ Mac, 2026-10-16).
const MAX_HELD = 100;
// Online guessing of a link password: global backoff on the sender side.
const AUTH_FAILURES_BEFORE_BACKOFF = 5;
const AUTH_BACKOFF_MS = 30_000;

export type PeerEntry = {
  id: string;
  session: PeerSession;
  authed: boolean;
  held: { msgs: unknown[]; channels: RTCDataChannel[] };
  leaveTimer?: ReturnType<typeof setTimeout>;
};

export type MeshOptions = {
  url: string;
  iceServers: RTCIceServer[];
  authKey?: CryptoKey | null;
  log?: (line: string) => void;
};

export class Mesh extends EventTarget {
  readonly self = randomHex(8);
  readonly peers = new Map<string, PeerEntry>();
  readonly room: RoomClient;
  readonly #opts: MeshOptions;
  #authFailures = 0;
  #authBlockedUntil = 0;
  #onOnline = () => this.peers.forEach((p) => void p.session.iceRestart('online'));

  constructor(opts: MeshOptions) {
    super();
    this.#opts = opts;
    this.room = new RoomClient(`${opts.url}${opts.url.includes('?') ? '&' : '?'}peer=${this.self}`);
    // End-to-end tests inject this set before the page loads to reach the live meshes.
    (globalThis as { __dropMeshes?: Set<Mesh> }).__dropMeshes?.add(this);
  }

  log(line: string): void {
    this.#opts.log?.(line);
  }

  async start(): Promise<void> {
    const room = this.room;
    room.addEventListener('join', (e) => {
      const { peer } = (e as CustomEvent).detail as { peer: string };
      const known = this.peers.get(peer);
      if (!known) return void this.#add(peer);
      // A known peer joining again lost its socket — most likely it changed networks.
      clearTimeout(known.leaveTimer);
      known.session.resync('peer-rejoined');
      void known.session.iceRestart('peer-rejoined');
    });
    room.addEventListener('welcome', (e) => {
      const { peers, reconnect } = (e as CustomEvent).detail as { peers: string[]; reconnect: boolean };
      for (const peer of peers) {
        const known = this.peers.get(peer);
        if (!known) this.#add(peer);
        else if (reconnect) {
          known.session.resync('signaling-reconnect');
          void known.session.iceRestart('signaling-reconnect');
        }
      }
    });
    room.addEventListener('leave', (e) => {
      const { peer } = (e as CustomEvent).detail as { peer: string };
      const entry = this.peers.get(peer);
      if (!entry) return;
      entry.session.ev('peer:left');
      clearTimeout(entry.leaveTimer);
      entry.leaveTimer = setTimeout(() => {
        if (entry.session.pc?.connectionState !== 'connected') this.#remove(peer);
      }, LEAVE_GRACE_MS);
    });
    room.addEventListener('signal', (e) => {
      const { from, data } = (e as CustomEvent).detail as { from: string; data: Signal };
      const entry = this.peers.get(from) ?? this.#add(from);
      void entry.session.onSignal(data);
    });
    room.addEventListener('closed', (e) => {
      const { code } = (e as CustomEvent).detail as { code: number };
      this.peers.forEach((p) => p.session.ev(`ws:closed:${code}`));
    });
    addEventListener('online', this.#onOnline);
    await room.connect();
  }

  get authedPeers(): PeerEntry[] {
    return [...this.peers.values()].filter((p) => p.authed && p.session.ready);
  }

  send(peer: string, msg: unknown): boolean {
    const entry = this.peers.get(peer);
    return !!entry?.authed && entry.session.sendCtl(msg);
  }

  /** Opens a data channel to an authenticated peer and resolves once it is open. */
  openChannel(peer: string, label: string, timeoutMs = 20_000): Promise<RTCDataChannel> {
    const entry = this.peers.get(peer);
    const pc = entry?.session.pc;
    if (!entry?.authed || !pc) return Promise.reject(new Error('peer not ready'));
    const dc = pc.createDataChannel(label, { ordered: true });
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('channel open timeout')), timeoutMs);
      dc.onopen = () => {
        clearTimeout(timer);
        resolve(dc);
      };
      dc.onerror = () => {
        clearTimeout(timer);
        reject(new Error('channel error'));
      };
    });
  }

  close(): void {
    removeEventListener('online', this.#onOnline);
    this.peers.forEach((p) => p.session.close());
    this.peers.clear();
    this.room.close();
  }

  #emit(type: string, detail: Record<string, unknown>): void {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  #add(peer: string): PeerEntry {
    const session = new PeerSession(this.room, this.self, peer, this.#opts.iceServers, (l) => this.log(`${peer.slice(0, 6)} ${l}`));
    const entry: PeerEntry = { id: peer, session, authed: false, held: { msgs: [], channels: [] } };
    this.peers.set(peer, entry);

    session.addEventListener('ready', () => {
      entry.authed = false;
      this.#dropHeld(entry);
      if (!this.#opts.authKey) {
        entry.authed = true;
        this.#emit('authed', { peer });
      } else void this.#sendProof(entry);
    });
    session.addEventListener('down', () => {
      entry.authed = false;
      this.#dropHeld(entry);
      this.#emit('down', { peer });
    });
    session.addEventListener('state', (e) => this.#emit('state', { peer, state: (e as CustomEvent).detail.state }));
    session.addEventListener('ctl', (e) => {
      const msg = (e as CustomEvent).detail.msg as { t?: string; proof?: string };
      if (msg?.t === 'auth') return void this.#checkProof(entry, msg.proof);
      if (entry.authed) this.#emit('ctl', { peer, msg });
      else if (this.#opts.authKey && entry.held.msgs.length < MAX_HELD) entry.held.msgs.push(msg);
    });
    session.addEventListener('channel', (e) => {
      const channel = (e as CustomEvent).detail.channel as RTCDataChannel;
      if (entry.authed) this.#emit('channel', { peer, channel });
      else if (this.#opts.authKey && entry.held.channels.length < MAX_HELD) entry.held.channels.push(channel);
      else channel.close();
    });
    session.start();
    this.#emit('peer', { peer });
    return entry;
  }

  #remove(peer: string): void {
    const entry = this.peers.get(peer);
    if (!entry) return;
    entry.session.close();
    this.peers.delete(peer);
    this.#emit('gone', { peer });
  }

  #dropHeld(entry: PeerEntry): void {
    for (const channel of entry.held.channels) channel.close();
    entry.held = { msgs: [], channels: [] };
  }

  #fingerprints(entry: PeerEntry): [string, string] | null {
    const pc = entry.session.pc;
    const local = sdpFingerprint(pc?.localDescription?.sdp);
    const remote = sdpFingerprint(pc?.remoteDescription?.sdp);
    return local && remote ? [local, remote] : null;
  }

  async #sendProof(entry: PeerEntry): Promise<void> {
    const fps = this.#fingerprints(entry);
    if (!fps || !this.#opts.authKey) return;
    entry.session.sendCtl({ t: 'auth', proof: await makeProof(this.#opts.authKey, ...fps) });
  }

  async #checkProof(entry: PeerEntry, proof: unknown): Promise<void> {
    const fps = this.#fingerprints(entry);
    if (!this.#opts.authKey || !fps || typeof proof !== 'string') return;
    if (Date.now() < this.#authBlockedUntil) return;
    if (await checkProof(this.#opts.authKey, proof, ...fps)) {
      this.#authFailures = 0;
      entry.authed = true;
      entry.session.ev('auth:ok');
      this.#emit('authed', { peer: entry.id });
      const { msgs, channels } = entry.held;
      entry.held = { msgs: [], channels: [] };
      for (const msg of msgs) this.#emit('ctl', { peer: entry.id, msg });
      for (const channel of channels) this.#emit('channel', { peer: entry.id, channel });
      return;
    }
    entry.session.ev('auth:failed');
    this.#dropHeld(entry);
    if (++this.#authFailures >= AUTH_FAILURES_BEFORE_BACKOFF) {
      this.#authBlockedUntil = Date.now() + AUTH_BACKOFF_MS;
      this.#authFailures = 0;
    }
    this.#emit('auth-failed', { peer: entry.id });
  }
}
